// Quiet, self-dismissing toasts for the live channel: a CRM handoff arriving
// should SAY SO on whatever screen is open, without an alert dialog demanding
// a click. One stack, bottom of the screen, six seconds each, a link to where
// the news lives. Mounted once in AppRoutes, signed-in only.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Bell, X } from 'lucide-react';
import { useI18n } from '@binaa-almajal/ui-kit/hooks/useI18n';
import { useLiveEvent, LiveEvents } from './useLive';

const DISMISS_AFTER_MS = 6000;

let seq = 0;
let listener = null;

/** @param {{ messageKey: string, to?: string, linkKey?: string }} toast */
export function pushToast(toast) {
    listener?.({ id: (seq += 1), ...toast });
}

export default function LiveToaster() {
    const { t } = useI18n();
    const [toasts, setToasts] = useState([]);

    useEffect(() => {
        listener = (toast) => {
            setToasts((all) => [...all, toast]);
            setTimeout(() => {
                setToasts((all) => all.filter((item) => item.id !== toast.id));
            }, DISMISS_AFTER_MS);
        };
        return () => { listener = null; };
    }, []);

    useLiveEvent(LiveEvents.crmArrival, () => pushToast({
        messageKey: 'toast.crmArrival',
        to: '/people',
        linkKey: 'toast.viewPeople',
    }));

    if (toasts.length === 0) return null;

    return (
        <div
            aria-live="polite"
            className="pointer-events-none fixed bottom-4 left-1/2 z-[9000] flex w-full max-w-sm -translate-x-1/2 flex-col gap-2 px-4"
        >
            {toasts.map((toast) => (
                <div
                    key={toast.id}
                    className="pointer-events-auto flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm shadow-modal dark:border-gray-600 dark:bg-gray-800"
                >
                    <Bell className="h-4 w-4 shrink-0 text-primary-500" aria-hidden="true" />
                    <span className="min-w-0 flex-1 text-gray-800 dark:text-gray-100">
                        {t(toast.messageKey)}
                        {toast.to && (
                            <>
                                {' '}
                                <Link to={toast.to} className="font-medium text-primary-600 underline dark:text-primary-400">
                                    {t(toast.linkKey)}
                                </Link>
                            </>
                        )}
                    </span>
                    <button
                        type="button"
                        aria-label={t('common.close')}
                        onClick={() => setToasts((all) => all.filter((item) => item.id !== toast.id))}
                        className="focus-visible-ring shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                </div>
            ))}
        </div>
    );
}
